/**
 * Text markup — apply highlight / underline / strikethrough to the PDF text
 * runs a selection rectangle covers. The page has no selectable text layer, so
 * the user sweeps a rectangle over text and we snap the markup to the actual
 * runs underneath (word/segment granularity from pdf.js).
 *
 * Pure + byte-preserving: emits ONE semantic `add-text-markup` edit per gesture,
 * carrying every covered run rect. The driver turns it into a real PDF
 * /Highlight, /Underline, or /StrikeOut annotation with /QuadPoints, so other
 * viewers (Acrobat, Preview, mobile) recognize it as text markup — and the
 * underline/strikeout rule position comes from the annotation type, not a drawn
 * line, so we no longer offset a thin rule by hand.
 */
import type { Edit } from '@pdf-ticket/pdf-core';
import type { PdfRect, TextItemGeom } from './search.js';

export type MarkupKind = 'highlight' | 'underline' | 'strikeout';

export interface Rgb {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

/** Text runs whose rect overlaps the selection rect (PDF space, origin bottom-left). */
export function runsInRect(items: readonly TextItemGeom[], sel: PdfRect): PdfRect[] {
  const sx0 = sel.x;
  const sx1 = sel.x + sel.width;
  const sy0 = sel.y;
  const sy1 = sel.y + sel.height;
  const out: PdfRect[] = [];
  for (const it of items) {
    const r = it.rect;
    const xOverlap = r.x < sx1 && r.x + r.width > sx0;
    const yOverlap = r.y < sy1 && r.y + r.height > sy0;
    if (xOverlap && yOverlap) out.push(r);
  }
  return out;
}

/**
 * Build the markup edit for one page over the given run rects. Returns a single
 * `add-text-markup` edit carrying every run rect (the PDF annotation model is
 * one markup object with multiple /QuadPoints quads). Returns [] when no run is
 * covered, so the caller can skip an empty gesture.
 */
export function markupEditsForRuns(
  pageIndex: number,
  runRects: readonly PdfRect[],
  kind: MarkupKind,
  color: Rgb,
): Edit[] {
  if (runRects.length === 0) return [];
  return [
    {
      kind: 'add-text-markup',
      page: pageIndex,
      markup: kind,
      rects: runRects.map((r) => ({ x: r.x, y: r.y, width: r.width, height: r.height })),
      color,
    },
  ];
}
